import { useState } from 'react';
import { useMessages } from './i18n/context';
import type { AccessLevel } from './api/types';

/** Whether the caller may change the document (add/edit/move/remove, rename, sub-planlets). */
export function canEdit(access: AccessLevel): boolean {
  return access === 'owner' || access === 'write';
}

/**
 * The open plan's title bar: its name (the id only while none is known — a host that stamps
 * no `X-Resource-Name`), version, the caller's access badge, and the actions that access
 * allows — rename (≥ Write), share, move and delete (Owner), "make it mine" (Read: a frozen
 * legacy plan, or a reader wanting an owned copy — ADR-027 §7 import).
 */
export function PlanHeader({
  planId,
  name,
  version,
  access,
  busy,
  onRename,
  onShare,
  onMove,
  onDelete,
  onImport,
  onClose,
}: {
  planId: string;
  name: string | null;
  version: number;
  access: AccessLevel;
  busy: boolean;
  onRename: (name: string) => void;
  onShare: () => void;
  onMove: () => void;
  onDelete: () => void;
  onImport: () => void;
  onClose: () => void;
}) {
  const t = useMessages();
  const g = t.grove;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const title = name ?? planId;
  const trimmed = draft.trim();

  return (
    <header className="plan-header">
      <div className="plan-heading">
        <h2 className="plan-title">{title}</h2>
        <div className="plan-meta">
          <span className="access-badge" data-access={access} aria-label={t.header.yourAccess}>
            {t.access[access]}
          </span>
          <span className="plan-version" aria-label={t.header.planVersion}>v{version}</span>
        </div>
      </div>
      <div className="plan-actions" role="group" aria-label={g.planActions}>
        {canEdit(access) && (
          <button
            className="button-secondary"
            disabled={busy}
            onClick={() => {
              setDraft(title);
              setEditing(true);
            }}
          >
            {t.header.rename}
          </button>
        )}
        {access === 'owner' && (
          <>
            <button className="button-primary" disabled={busy} onClick={onShare}>
              {t.header.share}
            </button>
            <button className="button-quiet" disabled={busy} onClick={onMove}>
              {t.header.move}
            </button>
            <button className="button-danger" disabled={busy} onClick={onDelete}>
              {t.header.delete}
            </button>
          </>
        )}
        {access === 'read' && (
          <button className="button-primary" disabled={busy} onClick={onImport}>
            {t.header.makeItMine}
          </button>
        )}
        <button className="button-quiet" onClick={onClose} disabled={busy}>
          {t.header.close}
        </button>
      </div>
      {editing && (
        <form
          className="inline-form"
          aria-label={t.header.rename}
          onSubmit={(e) => {
            e.preventDefault();
            if (trimmed !== '') {
              setEditing(false);
              onRename(trimmed);
            }
          }}
        >
          <input
            aria-label={t.header.newPlanName}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          <button className="button-primary" type="submit" disabled={trimmed === ''}>
            {t.header.save}
          </button>
          <button className="button-quiet" type="button" onClick={() => setEditing(false)}>
            {t.header.cancel}
          </button>
        </form>
      )}
    </header>
  );
}
